import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
const API = process.env.REACT_APP_API_URL || '';

// Error boundary to catch React crashes and show error message instead of blank page
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('ONUAuthorizationPage crash:', error, info?.componentStack); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: '#c62828', fontFamily: 'monospace' }}>
          <h3>⚠️ Page Error</h3>
          <pre style={{ textAlign: 'left', background: '#fff3f3', padding: 16, borderRadius: 8, fontSize: 11, overflow: 'auto', maxHeight: 300 }}>
            {this.state.error.toString()}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 16, padding: '8px 20px', background: '#1565C0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ONU_TYPES = ['EG8141A5','EG8145V5','EG8247H','HG8245H','HG8346M','HG8546M','HG8145V','HG8145V5','HG8010H','MA5671A','MA5672A','MA5821','EG8010H','EG8145X6-10','EG8247X','Other'];
const SPEEDS = ['1M','5M','10M','25M','50M','100M','200M','500M','1G'];
const LINE_PROFILES = [
  {id:0,name:'PRIO mapping',hint:'Priority-based GEM map: PRIO 0,7 → GEM 1. PRIO 2 → GEM 2. PRIO 5 → GEM 3'},
  {id:1,name:'VLAN mapping',hint:'VLAN-based GEM map: Internet VLANs → GEM 1, Management/VoIP VLANs → GEM 2, IPTV → GEM 3'},
  {id:2,name:'SmartOLT_G_V20',hint:'gen1/1:28'},
  {id:3,name:'SmartOLT_G_V9',hint:'gen1/1:9'},
  {id:4,name:'Default profile',hint:'Single GEM port, all traffic'},
];
const SERVICE_PROFILES = [
  {id:0,name:'Default (EG8141A5)'},
  {id:1,name:'Default (EG8145V5)'},
  {id:2,name:'Default (HG8245H)'},
  {id:3,name:'Custom'},
];
const ZONES = ['KBA – Kamayut Township','BHN – Bahan Township','TGG – Thingangyun Township','EDG – East Dagon Township','WDG – West Dagon Township','MDG – North Dagon Township','SDG – South Dagon Township','HLG – Hlaing Township','HLGTH – Hlaing Tharyar Township','SAN – Sanchaung Township','LTT – Latha Township','PKK – Pabedan Township','LMD – Lanmadaw Township','AHL – Ahlone Township','DGN – Dagon Township','MHT – Mingalartaungnyunt Township','TKT – Thaketa Township','SYT – Shwe Pyi Thar Township','INS – Insein Township','MYG – Mayangone Township','PBD – Pazundaung Township','TDG – Tamwe Township','YDG – Yankin Township','MDN – Mingaladon Township'];
const VLANS = [9,10,11,13,18,20,22,25,40,41,44,50,98,99,1000,1040,2030,2060,2070,2080,3065,3606].map(v => ({id:v,name:`${v}`}));

// ─── Model Icon Mapping (extensible) ─────────────────────────────────────────
// ─── Model Product Image Mapping ─────────────────────────────────────────────
const MODEL_IMAGE_MAP = {
  'HS8145C5':    '/assets/onu-models/hs8145c5.png',
  'HG8145V':     '/assets/onu-models/hg8145v.png',
  'HG8145V5':    '/assets/onu-models/hg8145v.png',
  'HG8546M':     '/assets/onu-models/hg8546m.png',
  'HG8245W5-6T': '/assets/onu-models/hg8245w5-6t.png',
  'HG8245W5':    '/assets/onu-models/hg8245w5-6t.png',
  'EG8141A5':    '/assets/onu-models/eg8141a5.png',
  'EG8145V5':    '/assets/onu-models/eg8145v5.png',
  'HG8010H':     '/assets/onu-models/hg8010h.png',
  'EG8010H':     '/assets/onu-models/hg8010h.png',
  'HG8245H':     '/assets/onu-models/hg8245h.png',
  'MA5671A':     '/assets/onu-models/ma5671a.png',
  'MA5672A':     '/assets/onu-models/ma5671a.png',
  'HG8545M5':    '/assets/onu-models/hg8545m5.png',
  'HG8346M':     '/assets/onu-models/hg8545m5.png',
};
function getModelImage(model) {
  if (!model) return null;
  if (MODEL_IMAGE_MAP[model]) return MODEL_IMAGE_MAP[model];
  const key = Object.keys(MODEL_IMAGE_MAP).find(k => model.startsWith(k));
  return key ? MODEL_IMAGE_MAP[key] : null;
}

const MODEL_ICON_MAP = {
  // WiFi ONT models
  'HG8245W5-6T': { icon: 'wifi-router', hasWifi: true, label: 'WiFi ONT' },
  'HG8245W':     { icon: 'wifi-router', hasWifi: true, label: 'WiFi ONT' },
  'HG8245H':     { icon: 'wifi-router', hasWifi: true, label: 'WiFi ONT' },
  'EG8245X':     { icon: 'wifi-router', hasWifi: true, label: 'WiFi ONT' },
  'EG8247X':     { icon: 'wifi-router', hasWifi: true, label: 'WiFi ONT' },
  'EG8247H':     { icon: 'wifi-router', hasWifi: true, label: 'WiFi ONT' },
  // GPON+WiFi combo
  'HG8546M':     { icon: 'gpon-wifi', hasWifi: true, label: 'GPON+WiFi' },
  'HG8346M':     { icon: 'gpon-wifi', hasWifi: true, label: 'GPON+WiFi' },
  'EG8141A5':    { icon: 'gpon-wifi', hasWifi: true, label: 'GPON+WiFi' },
  'EG8145V5':    { icon: 'gpon-wifi', hasWifi: true, label: 'GPON+WiFi' },
  'EG8145X6-10': { icon: 'gpon-wifi', hasWifi: true, label: 'GPON+WiFi' },
  // Single-port ONT
  'HG8145V':     { icon: 'router', hasWifi: false, label: 'Single-port ONT' },
  'HG8145V5':    { icon: 'router', hasWifi: false, label: 'Single-port ONT' },
  'HS8145C5':    { icon: 'router', hasWifi: false, label: 'Single-port ONT' },
  'HG8010H':     { icon: 'router', hasWifi: false, label: 'ONT' },
  'EG8010H':     { icon: 'router', hasWifi: false, label: 'ONT' },
  'MA5671A':     { icon: 'router', hasWifi: false, label: 'SFP ONT' },
  'MA5672A':     { icon: 'router', hasWifi: false, label: 'SFP ONT' },
  'MA5821':      { icon: 'router', hasWifi: false, label: 'ONT' },
};

function getModelInfo(model) {
  if (!model) return { icon: 'device', hasWifi: false, label: 'Unknown' };
  // Exact match first
  if (MODEL_ICON_MAP[model]) return MODEL_ICON_MAP[model];
  // Prefix match
  const key = Object.keys(MODEL_ICON_MAP).find(k => model.startsWith(k.slice(0, 6)));
  if (key) return MODEL_ICON_MAP[key];
  // Suffix-based heuristic
  if (/W5|W6|W-|245/.test(model)) return { icon: 'wifi-router', hasWifi: true, label: 'WiFi ONT' };
  if (/546|346|141|145/.test(model)) return { icon: 'gpon-wifi', hasWifi: true, label: 'GPON+WiFi' };
  return { icon: 'device', hasWifi: false, label: 'ONT' };
}

// ─── Port color palette ───────────────────────────────────────────────────────
const PORT_COLORS = [
  { bg: '#e3f2fd', text: '#1565C0', border: '#90caf9' },
  { bg: '#f3e5f5', text: '#6a1b9a', border: '#ce93d8' },
  { bg: '#e8f5e9', text: '#2e7d32', border: '#a5d6a7' },
  { bg: '#fff3e0', text: '#e65100', border: '#ffcc80' },
  { bg: '#fce4ec', text: '#880e4f', border: '#f48fb1' },
  { bg: '#e0f7fa', text: '#006064', border: '#80deea' },
];
function getPortColor(port, portList) {
  const idx = portList.indexOf(port);
  return PORT_COLORS[(idx >= 0 ? idx : 0) % PORT_COLORS.length];
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────
function IconRouter({ size = 32, color = '#1565C0' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="14" width="20" height="6" rx="2"/>
      <path d="M6 14V8a6 6 0 0 1 12 0v6"/>
      <circle cx="17" cy="17" r="1" fill={color} stroke="none"/>
      <circle cx="20" cy="17" r="1" fill={color} stroke="none"/>
    </svg>
  );
}
function IconWifiRouter({ size = 32, color = '#1565C0' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="14" width="20" height="6" rx="2"/>
      <path d="M5 11a7 7 0 0 1 14 0"/>
      <path d="M7.5 8.5a4 4 0 0 1 9 0"/>
      <path d="M10 6a1 1 0 0 1 4 0"/>
      <circle cx="17" cy="17" r="1" fill={color} stroke="none"/>
      <circle cx="20" cy="17" r="1" fill={color} stroke="none"/>
    </svg>
  );
}
function IconGponWifi({ size = 32, color = '#6a1b9a' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="14" width="20" height="6" rx="2"/>
      <path d="M5 11a7 7 0 0 1 14 0"/>
      <path d="M8 8.5a4.5 4.5 0 0 1 8 0"/>
      <circle cx="12" cy="6" r="1" fill={color} stroke="none"/>
      <circle cx="17" cy="17" r="1" fill={color} stroke="none"/>
      <circle cx="20" cy="17" r="1" fill={color} stroke="none"/>
    </svg>
  );
}
function IconDevice({ size = 32, color = '#546e7a' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2"/>
      <path d="M8 12h8M12 8v8"/>
    </svg>
  );
}
function IconCopy({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}
function IconCheck({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}
function IconChevron({ size = 16, down = true }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: down ? 'none' : 'rotate(-90deg)', transition: '0.2s' }}>
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}
function IconMoreVert({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/>
    </svg>
  );
}
function IconWifi({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
      <circle cx="12" cy="20" r="1" fill="currentColor" stroke="none"/>
    </svg>
  );
}
function IconScan({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
      <line x1="7" y1="12" x2="17" y2="12"/>
    </svg>
  );
}
function IconSync({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
    </svg>
  );
}

function ModelIcon({ model, size = 32 }) {
  const imgSrc = getModelImage(model);
  if (imgSrc) {
    return <img src={imgSrc} alt={model || 'ONU'} width={size} height={size} style={{ objectFit: 'contain', display: 'block', borderRadius: 4 }} />;
  }
  const info = getModelInfo(model);
  if (info.icon === 'wifi-router') return <IconWifiRouter size={size} color="#1565C0" />;
  if (info.icon === 'gpon-wifi') return <IconGponWifi size={size} color="#6a1b9a" />;
  if (info.icon === 'router') return <IconRouter size={size} color="#2e7d32" />;
  return <IconDevice size={size} color="#546e7a" />;
}

// ─── Copy-to-clipboard hook ───────────────────────────────────────────────────
function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return [copied, copy];
}

// ─── Loading Skeleton Card ────────────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '14px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14, border: '1px solid #e8eaf6', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ height: 14, width: '40%', borderRadius: 4, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite', marginBottom: 8 }} />
        <div style={{ height: 11, width: '70%', borderRadius: 4, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ width: 80, height: 30, borderRadius: 6, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
        <div style={{ width: 60, height: 30, borderRadius: 6, background: 'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────
function EmptyState({ onRefresh }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', background: '#fff', borderRadius: 12, border: '1px dashed #c5cae9' }}>
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#9fa8da" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
        <rect x="2" y="14" width="20" height="6" rx="2"/>
        <path d="M6 14V8a6 6 0 0 1 12 0v6"/>
        <path d="M5 11a7 7 0 0 1 14 0"/>
        <circle cx="17" cy="17" r="1" fill="#9fa8da" stroke="none"/>
        <circle cx="20" cy="17" r="1" fill="#9fa8da" stroke="none"/>
      </svg>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#3c4043', marginBottom: 8 }}>Unconfigured ONU မရှိပါ</div>
      <div style={{ fontSize: 13, color: '#80868b', marginBottom: 20, lineHeight: 1.6 }}>
        Fiber ချိတ်ပြီး ONU power ဖွင့်ထားပါ<br/>ပြီးရင် Refresh Autofind နှိပ်ပါ
      </div>
      <button onClick={onRefresh} style={{ background: '#1565C0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <IconScan size={14} /> Refresh Autofind
      </button>
    </div>
  );
}

// ─── ONU Card ─────────────────────────────────────────────────────────────────
function ONUCard({ onu, oltId, portColor, onAuthorize, onIgnore, selected, onSelect }) {
  const [copied, copy] = useCopyToClipboard();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const modelInfo = getModelInfo(onu.model);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  return (
    <div style={{
      background: '#fff',
      borderRadius: 10,
      padding: '12px 14px',
      marginBottom: 8,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      border: '1px solid #e8eaf6',
      borderLeft: '3px solid #f59e0b',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      transition: 'box-shadow 0.15s, transform 0.15s',
      position: 'relative',
    }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'none'; }}
    >
      {/* Checkbox */}
      <input type="checkbox" checked={selected} onChange={e => onSelect(e.target.checked)}
        style={{ width: 15, height: 15, cursor: 'pointer', flexShrink: 0, accentColor: '#1565C0' }} />

      {/* LEFT: Icon + status dot */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{ width: 72, height: 72, borderRadius: 10, background: modelInfo.hasWifi ? '#e3f2fd' : '#f1f8e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ModelIcon model={onu.model} size={60} />
        </div>
        {/* Status dot */}
        <span style={{ position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', background: '#f59e0b', border: '2px solid #fff', display: 'block' }} title="Detected / Unconfigured" />
      </div>

      {/* MIDDLE: Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Row 1: Model + WiFi badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1c1e' }}>{onu.model || 'Unknown Model'}</span>
          {modelInfo.hasWifi && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#e3f2fd', color: '#1565C0', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
              <IconWifi size={10} /> WiFi
            </span>
          )}
          {!modelInfo.hasWifi && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#f5f5f5', color: '#546e7a', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
              {modelInfo.label}
            </span>
          )}
        </div>
        {/* Row 2: SN + F/S/P + Vendor + SW */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#3c4043', background: '#f8f9fa', borderRadius: 4, padding: '1px 6px', border: '1px solid #e0e0e0' }}>
            {onu.sn}
          </span>
          <button onClick={() => copy(onu.sn)} title="Copy SN" style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#2e7d32' : '#9e9e9e', padding: '1px 3px', display: 'inline-flex', alignItems: 'center' }}>
            {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
          </button>
          {(onu.port || onu.frame_slot_port) && (
            <span style={{ background: portColor.bg, color: portColor.text, border: `1px solid ${portColor.border}`, borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>
              {onu.port || onu.frame_slot_port}
            </span>
          )}
          {onu.vendor && <span style={{ fontSize: 11, color: '#80868b' }}>{onu.vendor}</span>}
          {onu.sw_ver && <span style={{ fontSize: 11, color: '#9e9e9e' }}>v{onu.sw_ver}</span>}
          {onu.detected_at && <span style={{ fontSize: 11, color: '#bdbdbd' }}>• {onu.detected_at}</span>}
        </div>
      </div>

      {/* RIGHT: Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <button
          onClick={() => onAuthorize({ ...onu, olt_id: oltId })}
          style={{ background: '#1565C0', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}
        >
          ✓ Authorize
        </button>
        <button
          onClick={() => onIgnore(oltId, onu.sn)}
          style={{ background: '#fff', color: '#546e7a', border: '1px solid #cfd8dc', borderRadius: 7, padding: '7px 10px', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}
        >
          Ignore
        </button>
        {/* Kebab menu */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button onClick={() => setMenuOpen(p => !p)} style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 7, padding: '7px 8px', cursor: 'pointer', color: '#546e7a', display: 'inline-flex', alignItems: 'center' }}>
            <IconMoreVert size={14} />
          </button>
          {menuOpen && (
            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 140, overflow: 'hidden' }}>
              {[
                { label: '🔍 View details', action: () => { setMenuOpen(false); onAuthorize({ ...onu, olt_id: oltId }); } },
                { label: '📋 Copy SN', action: () => { copy(onu.sn); setMenuOpen(false); } },
              ].map(item => (
                <button key={item.label} onClick={item.action} style={{ display: 'block', width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: '#3c4043' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                  onMouseLeave={e => e.currentTarget.style.background = 'none'}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Port Group Section ───────────────────────────────────────────────────────
function PortGroup({ port, onus, oltId, portColor, onAuthorize, onIgnore, selectedSNs, onSelectSN, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const allSelected = onus.length > 0 && onus.every(o => selectedSNs.has(o.sn));
  const someSelected = onus.some(o => selectedSNs.has(o.sn));

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Section header */}
      <div
        onClick={() => setOpen(p => !p)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: portColor.bg, border: `1px solid ${portColor.border}`, borderRadius: 8, cursor: 'pointer', userSelect: 'none', marginBottom: open ? 8 : 0 }}
      >
        <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
          onChange={e => onus.forEach(o => onSelectSN(o.sn, e.target.checked))}
          onClick={e => e.stopPropagation()}
          style={{ width: 14, height: 14, cursor: 'pointer', accentColor: '#1565C0' }} />
        <span style={{ fontWeight: 700, fontSize: 13, color: portColor.text }}>Port {port}</span>
        <span style={{ background: portColor.text, color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}>{onus.length} ONU{onus.length !== 1 ? 's' : ''}</span>
        <span style={{ marginLeft: 'auto', color: portColor.text, opacity: 0.7 }}><IconChevron size={14} down={open} /></span>
      </div>
      {open && onus.map(onu => (
        <ONUCard
          key={onu.sn}
          onu={onu}
          oltId={oltId}
          portColor={portColor}
          onAuthorize={onAuthorize}
          onIgnore={onIgnore}
          selected={selectedSNs.has(onu.sn)}
          onSelect={checked => onSelectSN(onu.sn, checked)}
        />
      ))}
    </div>
  );
}

// ─── Scan Progress Console ────────────────────────────────────────────────────
function ScanProgress({ prog, olt }) {
  const elapsed = Number(prog?.elapsed || 0);
  const step = prog?.step || 'Establishing device discovery link';
  const status = prog?.status || 'running';
  const steps = Array.isArray(prog?.steps) ? prog.steps : [];
  const isError = status === 'error';
  const progress = Math.max(6, Math.min(92, (elapsed / 34) * 100));
  const recentSteps = steps.slice(-3);
  const stageLabel = isError ? 'DISCOVERY INTERRUPTED' : status === 'done' ? 'DISCOVERY COMPLETE' : 'ACTIVE DISCOVERY';
  const scanText = `${step} ${steps.map(item => item.step || '').join(' ')}`.toLowerCase();
  const phase = isError ? 0 : status === 'done' ? 6 : scanText.includes('done') ? 6 : scanText.includes('updat') || scanText.includes('detected') ? 5 : scanText.includes('pars') || scanText.includes('scanning') ? 4 : scanText.includes('display ont') || scanText.includes('running:') ? 3 : scanText.includes('connect') ? 2 : 1;
  const target = olt?.ip_address || olt?.ip || olt?.name || 'Selected OLT';
  const activity = [
    { code: '01', title: 'SESSION LINK', detail: `Secure SSH session to ${target}` },
    { code: '02', title: 'CLI COMMAND', detail: 'display ont autofind all' },
    { code: '03', title: 'OUTPUT CAPTURE', detail: 'Read prompt and continue CLI pages' },
    { code: '04', title: 'ONU PARSING', detail: 'Extract SN, F/S/P, model and vendor' },
    { code: '05', title: 'ERP SAFETY CHECK', detail: 'Exclude existing authorized ONUs' },
    { code: '06', title: 'RESULT SYNC', detail: 'Update pending ONU discovery cache' },
  ];

  return (
    <div className={`noc-scan-console${isError ? ' is-error' : ''}`}>
      <div className="noc-scan-grid" />
      <div className="noc-scan-topline">
        <span className="noc-scan-live-dot" />
        <span>{stageLabel}</span>
        <span className="noc-scan-divider" />
        <span>OLT AUTOFIND ENGINE</span>
        <span className="noc-scan-elapsed">{elapsed.toFixed(elapsed % 1 ? 1 : 0)}s elapsed</span>
      </div>

      <div className="noc-scan-main">
        <div className="noc-radar-shell" aria-hidden="true">
          <div className="noc-radar-ring ring-one" />
          <div className="noc-radar-ring ring-two" />
          <div className="noc-radar-sweep" />
          <div className="noc-radar-core">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="5" width="16" height="11" rx="2" />
              <path d="M8 19h8M12 16v3M8 9h.01M12 9h.01M16 9h.01" />
            </svg>
          </div>
        </div>

        <div className="noc-scan-status">
          <div className="noc-scan-label">CURRENT OPERATION</div>
          <div className="noc-scan-step">{step}</div>
          <div className="noc-scan-progress-track">
            <div className="noc-scan-progress-fill" style={{ width: `${progress}%` }} />
            <span className="noc-scan-progress-glow" style={{ left: `${progress}%` }} />
          </div>
          <div className="noc-scan-stages">
            <span className="is-active">LINK</span>
            <i />
            <span className={elapsed >= 3 ? 'is-active' : ''}>QUERY</span>
            <i />
            <span className={elapsed >= 7 ? 'is-active' : ''}>PARSE</span>
            <i />
            <span className={elapsed >= 11 ? 'is-active' : ''}>SYNC</span>
          </div>
        </div>

        <div className="noc-scan-metrics">
          <div><span>MODE</span><strong>{isError ? 'RETRY' : 'SSH'}</strong></div>
          <div><span>PHASE</span><strong>{Math.min(6, Math.max(1, phase))}/6</strong></div>
          <div><span>STATUS</span><strong className={isError ? 'error' : 'ok'}>{isError ? 'CHECK' : 'LIVE'}</strong></div>
        </div>
      </div>

      <div className="noc-scan-detail-zone">
        <div className="noc-detail-heading"><span>TECHNICIAN ACTIVITY</span><small>LIVE EXECUTION MAP</small></div>
        <div className="noc-activity-grid">
          {activity.map((item, index) => {
            const state = isError ? (index + 1 < phase ? 'done' : 'error') : index + 1 < phase ? 'done' : index + 1 === phase ? 'active' : 'queued';
            return (
              <div key={item.code} className={`noc-activity-card ${state}`}>
                <div className="noc-activity-state">{state === 'done' ? '✓' : state === 'error' ? '!' : item.code}</div>
                <div><strong>{item.title}</strong><span>{item.detail}</span></div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="noc-scan-log">
        <span className="noc-log-prompt">LIVE LOG</span>
        <div className="noc-log-lines">
          {recentSteps.length ? recentSteps.map((item, index) => (
            <div key={`${item.t}-${index}`} className={index === recentSteps.length - 1 ? 'current' : ''}>
              <span>{String(item.t ?? item.elapsed ?? elapsed).padStart(2, '0')}s</span>{item.step}
            </div>
          )) : <div className="current"><span>00s</span>Initializing scan telemetry…</div>}
        </div>
      </div>
    </div>
  );
}

function ScanEvidenceReport({ prog, olt }) {
  const result = prog?.result || {};
  const evidence = result.scan_evidence || {};
  const steps = Array.isArray(prog?.steps) ? prog.steps : [];
  const [detailsOpen, setDetailsOpen] = useState(false);
  const hasEvidence = Boolean(result.count !== undefined || evidence.raw_detected_count !== undefined || prog?.error);
  if (!hasEvidence) return null;
  const stageDefinitions = [
    { key: 'session_link', label: 'Access session', fallback: 'Open the saved OLT management session' },
    { key: 'query', label: 'Discovery query', fallback: evidence.query || 'display ont autofind all' },
    { key: 'collect', label: 'Output capture', fallback: 'Read the returned OLT discovery output' },
    { key: 'parse', label: 'Serial parsing', fallback: evidence.parser_note || 'Parse recognized ONU serial records' },
    { key: 'reconcile', label: 'Authorized exclusion', fallback: 'Keep existing authorized ONTs out of the pending list' },
    { key: 'result_sync', label: 'Result cache', fallback: 'Store the resulting pending ONU records' },
  ];
  const currentStage = prog?.stage || (prog?.status === 'done' ? 'result_sync' : '');
  const completedStages = new Set(steps.map(item => item.stage));
  const stageStep = key => steps.slice().reverse().find(item => item.stage === key);
  const telnetFallback = prog?.status === 'done' && evidence?.transport === 'telnet' && evidence?.snmp?.error;
  const faultText = prog?.error || evidence?.cli_fallback?.error || '';
  const returnedOnus = Array.isArray(result.onus) ? result.onus : [];
  const upsertFailures = Array.isArray(evidence.upsert_failures) ? evidence.upsert_failures : [];
  const counts = [
    ['OLT', evidence.raw_detected_count ?? '—'],
    ['Pending', evidence.returned_unconfigured_count ?? result.count ?? '—'],
    ['Excluded', evidence.skipped_authorized_count ?? '—'],
    ['Save errors', evidence.upsert_failure_count ?? '—'],
  ];
  return (
    <div style={{ margin: '0 0 14px', background: '#FFFFFF', border: `1px solid ${faultText ? '#FECACA' : '#D8E6F7'}`, borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,42,.06)' }}>
      <div style={{ minHeight: 58, padding: '9px 13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: faultText ? '#FFF7F7' : '#F6FAFF' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: '#17365D', fontSize: 11, fontWeight: 800, letterSpacing: '.05em' }}>SCAN RESULT</span>
            <span style={{ color: faultText ? '#B42318' : prog?.status === 'done' ? '#16803C' : '#175CD3', fontSize: 10, fontWeight: 800 }}>{faultText ? 'CHECK REQUIRED' : prog?.status === 'done' ? 'COMPLETED' : 'IN PROGRESS'}</span>
            {counts.map(([label, value]) => <span key={label} style={{ color: '#475467', fontSize: 10, fontWeight: 650, whiteSpace: 'nowrap' }}><strong style={{ color: '#182230', fontSize: 13, marginRight: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</strong>{label}</span>)}
          </div>
          <div style={{ marginTop: 4, color: '#667085', fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{olt?.name || 'Selected OLT'} · {evidence.cached ? `Cached result (${evidence.cache_age_seconds || 0}s old)` : `${result.method || evidence.transport || 'OLT'} query`} · {returnedOnus.length ? `${returnedOnus.length} ONU record${returnedOnus.length === 1 ? '' : 's'} ready for review` : 'No pending ONU record returned'}</div>
        </div>
        <button type="button" onClick={() => setDetailsOpen(open => !open)} style={{ flex: '0 0 auto', border: '1px solid #B2D4F7', borderRadius: 6, padding: '6px 9px', background: detailsOpen ? '#175CD3' : '#FFFFFF', color: detailsOpen ? '#FFFFFF' : '#175CD3', fontSize: 10, fontWeight: 800, cursor: 'pointer' }}>{detailsOpen ? 'HIDE DETAILS' : 'VIEW DETAILS'}</button>
      </div>
      {detailsOpen && <div style={{ borderTop: '1px solid #D8E6F7', background: '#FFFFFF', padding: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1.85fr)', gap: 12 }}>
          <div style={{ padding: 10, border: '1px solid #E4E7EC', borderRadius: 8, background: '#FCFCFD' }}>
            <div style={{ color: '#475467', fontSize: 10, fontWeight: 800, letterSpacing: '.06em', marginBottom: 7 }}>SCAN SCOPE</div>
            <div style={{ color: '#344054', fontSize: 11, lineHeight: 1.6 }}><strong>OLT:</strong> {olt?.name || 'Selected OLT'}<br/><strong>Command:</strong> {evidence.query || 'display ont autofind all'}<br/><strong>Transport:</strong> {result.method || evidence.transport || 'OLT'}{evidence.cached ? ` · cache age ${evidence.cache_age_seconds || 0}s` : ''}<br/><strong>Rule:</strong> {evidence.parser_note || 'Recognized serial records only'}</div>
          </div>
          <div>
            <div style={{ color: '#475467', fontSize: 10, fontWeight: 800, letterSpacing: '.06em', marginBottom: 7 }}>EXECUTION DETAIL</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 7 }}>
          {stageDefinitions.map((stage, index) => {
            const record = stageStep(stage.key);
            const active = currentStage === stage.key && prog?.status !== 'done';
            const complete = completedStages.has(stage.key);
            const stateColor = complete ? '#16803C' : active ? '#175CD3' : '#98A2B3';
            return <div key={stage.key} style={{ display: 'flex', gap: 8, padding: '7px 8px', border: `1px solid ${active ? '#B2DDFF' : '#EAECF0'}`, borderRadius: 7, background: active ? '#F5FAFF' : '#FFFFFF' }}>
              <span style={{ width: 20, height: 20, flex: '0 0 auto', display: 'grid', placeItems: 'center', borderRadius: '50%', color: '#fff', background: stateColor, fontSize: 10, fontWeight: 800 }}>{complete ? '✓' : index + 1}</span>
              <div><div style={{ color: '#344054', fontSize: 11, fontWeight: 750 }}>{stage.label}</div><div style={{ marginTop: 2, color: '#667085', fontSize: 10, lineHeight: 1.35 }}>{record?.detail || record?.step || stage.fallback}</div></div>
            </div>;
          })}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 12, color: '#475467', fontSize: 10, fontWeight: 800, letterSpacing: '.06em' }}>DISCOVERED PENDING ONU RECORDS</div>
        {returnedOnus.length ? <div style={{ marginTop: 7, overflowX: 'auto', border: '1px solid #EAECF0', borderRadius: 8 }}><table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 540, fontSize: 10 }}><thead><tr style={{ color: '#667085', background: '#F9FAFB', textAlign: 'left' }}><th style={{ padding: '7px 9px' }}>SERIAL</th><th style={{ padding: '7px 9px' }}>PORT</th><th style={{ padding: '7px 9px' }}>MODEL</th><th style={{ padding: '7px 9px' }}>VENDOR</th><th style={{ padding: '7px 9px' }}>DETECTED</th></tr></thead><tbody>{returnedOnus.map((onu, index) => <tr key={`${onu.sn || onu.serial || 'onu'}-${index}`} style={{ borderTop: '1px solid #EAECF0', color: '#344054' }}><td style={{ padding: '7px 9px', fontFamily: 'monospace', fontWeight: 700 }}>{onu.sn || onu.serial || '—'}</td><td style={{ padding: '7px 9px' }}>{onu.port || onu.fsp || '—'}</td><td style={{ padding: '7px 9px' }}>{onu.model || '—'}</td><td style={{ padding: '7px 9px' }}>{onu.vendor || '—'}</td><td style={{ padding: '7px 9px' }}>{onu.detected_at || result.scanned_at || 'Current scan'}</td></tr>)}</tbody></table></div> : <div style={{ marginTop: 7, padding: '9px 10px', borderRadius: 7, color: '#667085', background: '#F9FAFB', fontSize: 10 }}>No unconfigured ONU record was returned. The scan still completed and the counters above show the reconciliation outcome.</div>}
        {Number(evidence.skipped_authorized_count || 0) > 0 && <div style={{ marginTop: 9, padding: '8px 9px', borderRadius: 7, color: '#475467', background: '#F9FAFB', fontSize: 10, lineHeight: 1.4 }}><strong>Authorized exclusion:</strong> {evidence.skipped_authorized_count} existing authorized record(s) were deliberately kept out of this pending list. Review them in ONU Management; this scan never republishes them as unconfigured.</div>}
        {telnetFallback && <div style={{ marginTop: 10, padding: '8px 9px', borderRadius: 7, color: '#175CD3', background: '#EFF8FF', fontSize: 10, lineHeight: 1.4 }}><strong>Transport fallback:</strong> SNMP did not return a usable table, so the completed Telnet query was used for this result. Diagnostic: {evidence.snmp.error}</div>}
        {upsertFailures.length > 0 && <div style={{ marginTop: 10, padding: '8px 9px', borderRadius: 7, color: '#B42318', background: '#FEF3F2', fontSize: 10, lineHeight: 1.4 }}><strong>Save exception detail:</strong> {upsertFailures.map(item => `${item.serial || 'record'} — ${item.reason || 'unknown error'}`).join(' · ')}</div>}
        {faultText && <div style={{ marginTop: 10, padding: '8px 9px', borderRadius: 7, color: '#B42318', background: '#FEF3F2', fontSize: 10, lineHeight: 1.4 }}><strong>Scan exception:</strong> {faultText}</div>}
      </div>}
    </div>
  );
}

// ─── useApi ───────────────────────────────────────────────────────────────────
function useApi() {
  const apiRef = useRef(null);
  if (!apiRef.current) {
    apiRef.current = {
      get: (url) => {
        const token = localStorage.getItem('session_token') || '';
        const h = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
        return fetch(`${API}${url}`, { headers: h }).then(r => r.json());
      },
      post: (url, body) => {
        const token = localStorage.getItem('session_token') || '';
        const h = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
        return fetch(`${API}${url}`, { method: 'POST', headers: h, body: JSON.stringify(body) }).then(r => r.json());
      },
      put: (url, body) => {
        const token = localStorage.getItem('session_token') || '';
        const h = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
        return fetch(`${API}${url}`, { method: 'PUT', headers: h, body: JSON.stringify(body) }).then(r => r.json());
      },
      del: (url) => {
        const token = localStorage.getItem('session_token') || '';
        const h = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
        return fetch(`${API}${url}`, { method: 'DELETE', headers: h }).then(r => r.json());
      },
    };
  }
  return apiRef.current;
}

// ─── AuthorizeONUModal ────────────────────────────────────────────────────────
function AuthorizeONUModal({ onu, olts, zones: zonesProp = [], allowedVlans: allowedVlansProp = [], onClose, onSave }) {
  const fspParts = (onu?.port || onu?.frame_slot_port || '').split('/');
  const [form, setForm] = useState({
    olt_id: onu?.olt_id || (olts[0]?.id || ''),
    pon_type: 'GPON',
    gpon_channel: 'GPON',
    board: onu?.board || fspParts[1] || '',
    port: onu?.port_no != null ? String(onu.port_no) : (fspParts[2] || fspParts[1] || ''),
    fsp: onu?.port || onu?.frame_slot_port || '',
    sn: onu?.sn || onu?.onu_serial || '',
    ont_id: onu?.ont_id != null ? String(onu.ont_id) : '',
    onu_type: onu?.model || 'EG8141A5',
    custom_profile: false,
    line_profile_id: 0,
    service_profile_id: 0,
    // Bridge handoff is the standard production-safe Huawei HG path. ONU-side
    // Routing remains a future, separately approved per-ONU capability.
    onu_mode: 'Bridging',
    vlan_id: '',
    zone: '',
    zone_id: null,
    pppoe_next_counter: null,
    odb_splitter: onu?.odb_splitter || 'None',
    odb_port: 'None',
    download_speed: '10M',
    upload_speed: '1G',
    description: onu?.description || '',
    pppoe_username: onu?.pppoe_username || '',
    pppoe_password: '',
    dma_service_id: '',
    wan_profile_id: 0,
    policy_route_profile_id: 4,
    ip_index: 1,
    vlan_priority: 0,
    create_radius_account: true,
    address: '',
    external_id: '',
    use_gps: false,
  });
  const [pppoeGenerating, setPppoeGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const api2 = useApi();
  const [dmaServices, setDmaServices] = useState([]);

  useEffect(() => {
    api2.get('/api/noc/dma-services')
      .then(res => setDmaServices(Array.isArray(res?.items) ? res.items : []))
      .catch(() => setDmaServices([]));
  }, [api2]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const zones = zonesProp;
  const allowedVlans = allowedVlansProp;
  const [ontIdLoading, setOntIdLoading] = React.useState(false);
  // Query the OLT as soon as this modal is opened so an old autofind record can
  // never submit ONT ID 0 or a stale manually remembered ID.
  useEffect(() => {
    let cancelled = false;
    const loadFreeOntId = async () => {
      if (!form.olt_id || !form.fsp) return;
      setOntIdLoading(true);
      try {
        const r = await api2.get('/api/noc/onus/free-ont-id?olt_id=' + form.olt_id + '&port=' + encodeURIComponent(form.fsp));
        if (!cancelled && r && r.free_ont_id != null) {
          setForm(p => ({ ...p, ont_id: String(r.free_ont_id) }));
        }
      } catch (e) {
        // Leave the field editable; the authorization worker performs its own OLT-side collision check.
      } finally {
        if (!cancelled) setOntIdLoading(false);
      }
    };
    loadFreeOntId();
    return () => { cancelled = true; };
  }, [api2, form.olt_id, form.fsp]);

  // Manual refresh remains available if an operator changes the intended OLT/port.
  const autoDetectOntId = async () => {
    if (!form.olt_id || !form.fsp) { alert('OLT and Port are required'); return; }
    setOntIdLoading(true);
    try {
      const r = await api2.get('/api/noc/onus/free-ont-id?olt_id=' + form.olt_id + '&port=' + encodeURIComponent(form.fsp));
      if (r && r.free_ont_id != null) setForm(p => ({ ...p, ont_id: String(r.free_ont_id) }));
    } catch(e) { /* ignore */ }
    setOntIdLoading(false);
  };
  const selectedLine = LINE_PROFILES.find(l => l.id === form.line_profile_id);

  const IS = { width: '100%', border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 13, boxSizing: 'border-box', background: '#fff' };
  const LS = { fontWeight: 600, color: '#555', fontSize: 12, textAlign: 'right', paddingRight: 12, paddingTop: 8, whiteSpace: 'nowrap', width: 150, verticalAlign: 'top' };

  const generatePppoe = async () => {
    if (!form.zone_id && !form.zone) { alert('Please select Zone first'); return; }
    setPppoeGenerating(true);
    try {
      const zoneId = form.zone_id;
      const zoneName = form.zone;
      if (zoneId) {
        // Use new zone-based endpoint
        const res = await api2.post(`/api/noc/zones/${zoneId}/generate-pppoe`, {});
        if (!res?.success || !res?.username) throw new Error(res?.error || res?.detail || 'PPPoE ID generation failed');
        if (res.username) {
          set('pppoe_username', res.username);
          set('pppoe_password', res.username.toUpperCase());
          set('pppoe_next_counter', res.counter);
        }
      } else {
        // Fallback to old endpoint
        const res = await api2.post('/api/noc/pppoe/generate', { olt_id: form.olt_id, zone: form.zone, brand: 'MyanmarLink' });
        if (!res?.success || !res?.username) throw new Error(res?.error || res?.detail || 'PPPoE ID generation failed');
        if (res.username) { set('pppoe_username', res.username); set('pppoe_password', res.password); }
      }
    } catch(e) { alert('Failed to generate PPPoE ID: ' + e.message); }
    setPppoeGenerating(false);
  };

  // CLI preview
  const cliPreview = form.sn ? [
    `interface gpon 0/${form.board || '1'}`,
    ` ont add ${form.port || '4'} ${form.ont_id || '0'} sn-auth "${form.sn}" omci ont-lineprofile-id ${form.line_profile_id} ont-srvprofile-id ${form.service_profile_id} desc "${form.description || form.pppoe_username || ''}"`,
    form.vlan_id ? `service-port vlan ${form.vlan_id} gpon 0/${form.board || '1'}/${form.port || '4'} ont ${form.ont_id || '0'} gemport 0 multi-service user-vlan ${form.vlan_id}` : null,
    form.onu_mode === 'Routing' && form.vlan_id && form.pppoe_username ? `interface gpon 0/${form.board || '1'}` : null,
    form.onu_mode === 'Routing' && form.vlan_id && form.pppoe_username ? ` ont ipconfig ${form.port || '4'} ${form.ont_id || '0'} ip-index 1 pppoe vlan ${form.vlan_id} priority 0 user-account username ${form.pppoe_username} password ${form.pppoe_password || form.pppoe_username.toUpperCase()}` : null,
    form.onu_mode === 'Routing' && form.pppoe_username ? ` ont port route ${form.port || '4'} ${form.ont_id || '0'} eth 1 enable` : null,
  ].filter(Boolean).join('\n') : '';

  const handleSave = async () => {
    if (!form.sn) { alert('SN is required'); return; }
    setSaving(true); setSaveResult(null);
    try {
      const ontIdVal = parseInt(form.ont_id);
      if (!ontIdVal || ontIdVal <= 0) { setSaveResult({ success: false, error: 'ONT ID must be 1-127 (cannot be 0)' }); setSaving(false); return; }
      if (!form.vlan_id) { setSaveResult({ success: false, error: 'VLAN ID is required for service-port creation' }); setSaving(false); return; }
      if (form.onu_mode === 'Routing' && routingUnsupported) {
        setSaveResult({ success: false, code: 'ROUTING_MODE_UNSUPPORTED', error: bridgeGuidance }); setSaving(false); return;
      }
      if (form.onu_mode === 'Routing' && (!form.pppoe_username || !form.pppoe_password || !form.dma_service_id)) {
        setSaveResult({ success: false, error: 'Routing mode requires PPPoE username, password, and a DMA service plan' }); setSaving(false); return;
      }
      const res = await onSave({
        ...form,
        ont_id: ontIdVal,
        pppoe_password: form.pppoe_password || (form.pppoe_username ? form.pppoe_username.toUpperCase() : ''),
        create_radius_account: form.create_radius_account,
        name: form.description || form.pppoe_username || '',
        zone_id: form.zone_id,
        pppoe_next_counter: form.pppoe_next_counter,
        gemport_id: 1,
      });
      setSaveResult(res);
      if (res && (res.success || res.status === 'success' || res.telnet_result === 'success')) {
        setTimeout(() => { onClose(); }, 1500);
      }
    } catch(e) { setSaveResult({ success: false, error: String(e) }); }
    setSaving(false);
  };

  const routingUnsupported = Number(form.olt_id) === 1 && /^HG/i.test(String(form.onu_type || '').toUpperCase());
  const bridgeGuidance = 'Bridge handoff is the production standard for Huawei HG ONUs. Routing is deferred until U2000/NCE or a verified model-specific GenieACS workflow provides safe per-ONU scoping and rollback. The downstream router/CPE should dial PPPoE on VLAN 20.';
  const modelInfo = getModelInfo(form.onu_type);
  // Safe default: unsupported Huawei HG models must never open in ONU-side Routing mode.
  React.useEffect(() => {
    if (routingUnsupported && form.onu_mode === 'Routing') {
      setForm(p => ({ ...p, onu_mode: 'Bridging', pppoe_username: '', pppoe_password: '', dma_service_id: '' }));
    }
  }, [routingUnsupported, form.onu_mode]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '20px 0' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: 760, boxShadow: '0 12px 40px rgba(0,0,0,0.25)', margin: 'auto' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1565C0,#1976d2)', color: '#fff', padding: '16px 24px', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ModelIcon model={form.onu_type} size={24} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Authorize ONU</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>{form.sn || 'New ONU'} {form.fsp ? `· ${form.fsp}` : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', borderRadius: 8, width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* ONU Info Banner */}
        {form.sn && (
          <div style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0', padding: '10px 24px', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
            <span><strong>SN:</strong> <code style={{ background: '#e8eaf6', borderRadius: 4, padding: '1px 6px' }}>{form.sn}</code></span>
            {form.fsp && <span><strong>F/S/P:</strong> <span style={{ background: '#e3f2fd', color: '#1565C0', borderRadius: 10, padding: '1px 8px', fontWeight: 600 }}>{form.fsp}</span></span>}
            {form.onu_type && <span><strong>Model:</strong> {form.onu_type}</span>}
            {modelInfo.hasWifi && <span style={{ background: '#e3f2fd', color: '#1565C0', borderRadius: 10, padding: '1px 8px', fontSize: 11, fontWeight: 600 }}><IconWifi size={10} /> WiFi</span>}
          </div>
        )}

        <div style={{ padding: '20px 24px' }}>
          {saveResult && (
            <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: saveResult.success ? '#e8f5e9' : '#ffebee', color: saveResult.success ? '#2e7d32' : '#c62828', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
              {saveResult.success ? '✅' : '❌'}
              {saveResult.success ? `ONU authorized successfully! Telnet: ${saveResult.telnet || 'ok'}` : `Error: ${saveResult.error || 'Authorization failed'}`}
            </div>
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <tr><td style={LS}>OLT</td><td style={{ padding: '4px 0' }}>
                <select style={IS} value={form.olt_id} onChange={e => set('olt_id', e.target.value)}>
                  {olts.map(o => <option key={o.id} value={o.id}>{o.id} – {o.name}</option>)}
                </select>
              </td></tr>
              <tr><td style={LS}>PON type</td><td style={{ padding: '8px 0' }}>
                {['GPON', 'EPON'].map(v => (
                  <label key={v} style={{ marginRight: 16, fontSize: 13, cursor: 'pointer' }}>
                    <input type="radio" name="pon_type" value={v} checked={form.pon_type === v} onChange={() => set('pon_type', v)} style={{ marginRight: 4 }} />{v}
                  </label>
                ))}
              </td></tr>
              <tr><td style={LS}>F/S/P</td><td style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input style={{ ...IS, width: 80 }} placeholder="Board" value={form.board} onChange={e => set('board', e.target.value)} />
                  <input style={{ ...IS, width: 80 }} placeholder="Port" value={form.port} onChange={e => set('port', e.target.value)} />
                  <span style={{ fontSize: 11, color: '#888', paddingTop: 9 }}>e.g. Board=1, Port=4 → gpon 0/1, port 4</span>
                </div>
              </td></tr>
              <tr><td style={LS}>ONU SN</td><td style={{ padding: '4px 0' }}>
                <input style={IS} value={form.sn} onChange={e => set('sn', e.target.value)} placeholder="e.g. 48575443DC91E99A" />
              </td></tr>
              <tr><td style={LS}>ONT ID</td><td style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input style={{ ...IS, width: 100 }} type="number" min="1" max="127" placeholder="1" value={form.ont_id} onChange={e => set('ont_id', e.target.value)} />
                  <button type="button" onClick={autoDetectOntId} disabled={ontIdLoading} style={{ background: '#e3f2fd', color: '#1565C0', border: '1px solid #90caf9', borderRadius: 6, padding: '7px 10px', cursor: 'pointer', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>
                    {ontIdLoading ? '⏳...' : '🔍 Auto'}
                  </button>
                  {!ontIdLoading && form.ont_id && parseInt(form.ont_id) === 0 && <span style={{ fontSize: 11, color: '#d32f2f' }}>⚠️ ONT ID 0 not allowed</span>}
                  {!ontIdLoading && form.ont_id && parseInt(form.ont_id) > 0 && <span style={{ fontSize: 11, color: '#2e7d32' }}>✓</span>}
                </div>
                <span style={{ fontSize: 11, color: '#888' }}>ONT ID (1–127). Click Auto to detect free ID from OLT.</span>
              </td></tr>
              <tr><td style={LS}>ONU type</td><td style={{ padding: '4px 0' }}>
                <select style={IS} value={form.onu_type} onChange={e => set('onu_type', e.target.value)}>
                  {ONU_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
                <label style={{ display: 'block', fontSize: 12, color: '#555', marginTop: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.custom_profile} onChange={e => set('custom_profile', e.target.checked)} style={{ marginRight: 4 }} />
                  Use custom profile
                </label>
              </td></tr>
              <tr><td style={LS}>Profile</td><td style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#E3FCEF', border: '1px solid #ABF5D1', borderRadius: 6 }}>
                  <span style={{ fontSize: 13, color: '#006644' }}>✅ Using default preset (Line + Service profile auto-assigned)</span>
                  <a href="/noc-auth-presets" target="_blank" style={{ fontSize: 11, color: '#0052CC', textDecoration: 'underline', marginLeft: 'auto' }}>Change default →</a>
                </div>
              </td></tr>
              <tr><td style={LS}>ONU mode</td><td style={{ padding: '8px 0' }}>
                <div style={{ marginBottom: 8, padding: '8px 10px', background: '#e8f5e9', color: '#1b5e20', border: '1px solid #a5d6a7', borderRadius: 6, fontSize: 12, lineHeight: 1.45 }}>
                  <strong>Standard production path: Bridge handoff.</strong> Keep the ONU transparent and let the downstream router/CPE perform PPPoE and RADIUS authentication.
                </div>
                {['Bridging', 'Routing'].map(v => {
                  const disabled = v === 'Routing' && routingUnsupported;
                  return <label key={v} style={{ marginRight: 16, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
                    <input type="radio" name="onu_mode" value={v} checked={form.onu_mode === v} disabled={disabled} onChange={() => set('onu_mode', v)} style={{ marginRight: 4 }} />{v === 'Bridging' ? 'Bridge — standard' : 'Routing — deferred'}{disabled ? ' (blocked)' : ''}
                  </label>;
                })}
                {routingUnsupported && <div style={{ marginTop: 8, padding: '8px 10px', background: '#fff8e1', color: '#8a5a00', border: '1px solid #ffe08a', borderRadius: 6, fontSize: 12, lineHeight: 1.45 }}>
                  <strong>Routing is deferred for this Huawei HG ONU.</strong> {bridgeGuidance}
                  {form.onu_mode === 'Routing' && <button type="button" onClick={() => set('onu_mode', 'Bridging')} style={{ marginLeft: 8, border: 'none', borderRadius: 5, background: '#f59e0b', color: '#fff', padding: '4px 8px', fontWeight: 700, cursor: 'pointer' }}>Switch to Bridging</button>}
                </div>}
              </td></tr>
              <tr><td style={LS}>VLAN ID</td><td style={{ padding: '4px 0' }}>
                {allowedVlans.length > 0 ? (
                  <select style={{ ...IS, width: 200 }} value={form.vlan_id} onChange={e => set('vlan_id', e.target.value)}>
                    <option value="">— Select VLAN —</option>
                    {allowedVlans.map(v => <option key={v.id} value={v.vlan_id}>{v.vlan_id}{v.name ? ` — ${v.name}` : ''}</option>)}
                  </select>
                ) : (
                  <input style={{ ...IS, width: 140 }} type="number" min="1" max="4094" placeholder="e.g. 20" value={form.vlan_id} onChange={e => set('vlan_id', e.target.value)} />
                )}
                {allowedVlans.length === 0 && <span style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>No allowed VLANs configured. <a href="/noc-zones" target="_blank" style={{ color: '#0052CC' }}>Setup →</a></span>}
              </td></tr>
              <tr><td style={LS}>PPPoE Username</td><td style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={{ ...IS, flex: 1 }} placeholder={form.onu_mode === 'Bridging' ? 'Downstream router PPPoE username (optional)' : 'PPPoE username'} value={form.pppoe_username} onChange={e => set('pppoe_username', e.target.value)} />
                  <button onClick={generatePppoe} disabled={pppoeGenerating} title={form.onu_mode === 'Bridging' ? 'Generate a downstream PPPoE account reference' : 'Generate PPPoE username'} style={{ background: '#e3f2fd', color: '#1565C0', border: '1px solid #90caf9', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>
                    {pppoeGenerating ? '...' : '⚡ Auto'}
                  </button>
                </div>
              </td></tr>
              <tr><td style={LS}>PPPoE Password</td><td style={{ padding: '4px 0' }}>
                <input style={IS} placeholder={form.onu_mode === 'Bridging' ? 'Downstream router/CPE PPPoE password (optional)' : 'Leave blank = username uppercase'} value={form.pppoe_password} onChange={e => set('pppoe_password', e.target.value)} />
              </td></tr>
              {form.onu_mode === 'Bridging' && <tr><td style={LS}>Bridge handoff</td><td style={{ padding: '4px 0', fontSize: 12, color: '#546e7a' }}>Transparent VLAN service only. The values above are kept as downstream CPE / ERP reference fields; this ONU is not configured for Routing mode.</td></tr>}
              <tr><td style={LS}>Internet Plan</td><td style={{ padding: '4px 0' }}>
                <select style={IS} value={form.dma_service_id} onChange={e => set('dma_service_id', e.target.value)}>
                  <option value="">— Select Radius / DMA service plan —</option>
                  {dmaServices.map(service => <option key={service.srvid} value={service.srvid}>{service.srvname}{service.downrate ? ` (${Math.round(service.downrate / 1048576)} Mbps)` : ''}</option>)}
                </select>
                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{form.onu_mode === 'Routing' ? 'Creates or validates the Radius account before ONU service provisioning. Internet-ready success requires this plan.' : 'Optional Bridge handoff service reference. The downstream router/CPE performs PPPoE; no ONU-side Routing command is sent.'}</div>
              </td></tr>
              <tr><td style={LS}>Description</td><td style={{ padding: '4px 0' }}>
                <input style={IS} placeholder="Customer name / location" value={form.description} onChange={e => set('description', e.target.value)} />
              </td></tr>
              <tr><td style={LS}>Zone / Area</td><td style={{ padding: '4px 0' }}>
                <select style={IS} value={form.zone_id || ''} onChange={e => {
                  const zid = e.target.value;
                  const zobj = zones.find(z => String(z.id) === String(zid));
                  set('zone_id', zid ? parseInt(zid) : null);
                  set('zone', zobj ? zobj.name : '');
                }}>
                  <option value="">— Select Zone —</option>
                  {zones.length > 0 ? zones.map(z => <option key={z.id} value={z.id}>{z.name}{z.pppoe_prefix ? ` (${z.pppoe_prefix})` : ''}</option>) : ZONES.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
                {form.zone_id && zones.find(z => z.id === form.zone_id)?.pppoe_prefix && (
                  <div style={{ fontSize: 11, color: '#1565C0', marginTop: 3 }}>PPPoE prefix: {zones.find(z => z.id === form.zone_id).pppoe_prefix}-XXXX</div>
                )}
              </td></tr>
              <tr><td style={LS}>ODB Splitter</td><td style={{ padding: '4px 0' }}>
                <select style={IS} value={form.odb_splitter} onChange={e => { set('odb_splitter', e.target.value); set('odb_port', 'None'); }}>
                  <option>None</option>
                  <option>KBA-FAT-A01</option><option>KBA-FAT-A02</option>
                  <option>PK-FAT-B01</option><option>PK-FAT-B02</option>
                  <option>EDG-FAT-D01</option><option>EDG-FAT-D02</option>
                </select>
              </td></tr>
              <tr><td style={LS}>ODB port</td><td style={{ padding: '4px 0' }}>
                <select style={IS} value={form.odb_port} onChange={e => set('odb_port', e.target.value)}>
                  <option>None</option>
                  {form.odb_splitter !== 'None' && Array.from({ length: 16 }, (_, i) => <option key={i + 1}>Port {i + 1}</option>)}
                </select>
              </td></tr>
              <tr><td style={LS}>Download speed</td><td style={{ padding: '4px 0' }}>
                <select style={IS} value={form.download_speed} onChange={e => set('download_speed', e.target.value)}>
                  {SPEEDS.map(s => <option key={s}>{s}</option>)}
                </select>
              </td></tr>
              <tr><td style={LS}>Upload speed</td><td style={{ padding: '4px 0' }}>
                <select style={IS} value={form.upload_speed} onChange={e => set('upload_speed', e.target.value)}>
                  {SPEEDS.map(s => <option key={s}>{s}</option>)}
                </select>
              </td></tr>
              {form.onu_mode === 'Routing' && <tr><td style={LS}>Provisioning</td><td style={{ padding: '8px 0' }}>
                <div style={{ background: '#e8f5e9', color: '#1b5e20', border: '1px solid #a5d6a7', borderRadius: 6, padding: '8px 10px', fontSize: 12 }}>PPPoE credentials and the selected DMA plan are validated first; only then does the system create the OLT service path.</div>
              </td></tr>}
            </tbody>
          </table>

          {/* CLI Preview */}
          {cliPreview && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#546e7a', marginBottom: 6 }}>CLI Command Preview</div>
              <pre style={{ background: '#1a1a2e', color: '#00e676', borderRadius: 8, padding: '12px 16px', fontSize: 11, margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.6 }}>{cliPreview}</pre>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 20, paddingTop: 16, borderTop: '1px solid #e0e0e0' }}>
            <button onClick={handleSave} disabled={saving} style={{ background: saving ? '#90caf9' : '#1565C0', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
              {saving ? '⏳ Authorizing...' : '✓ Authorize ONU'}
            </button>
            <button onClick={onClose} style={{ background: '#fff', color: '#546e7a', border: '1px solid #cfd8dc', borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── UnconfiguredTab ──────────────────────────────────────────────────────────
function UnconfiguredTab({ olts, api, tabs, onTabChange }) {
  const [selectedOlts, setSelectedOlts] = useState('all');
  const [scanResults, setScanResults] = useState({});
  const [scanning, setScanning] = useState({});
  const [scanProgress, setScanProgress] = useState({});
  const [scanReports, setScanReports] = useState({});
  const [syncing, setSyncing] = useState({});
  const [authorizeONU, setAuthorizeONU] = useState(null);
  const [toast, setToast] = useState(null);
  // Card UI state
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('port'); // port | model | detected
  const [selectedSNs, setSelectedSNs] = useState(new Set());
  const [bulkAuthorizing, setBulkAuthorizing] = useState(false);
  const [zones, setZones] = useState([]);
  const [allowedVlans, setAllowedVlans] = useState([]);
  // Load zones and allowed VLANs once
  React.useEffect(() => {
    api.get('/api/noc/zones?active_only=true').then(r => {
      if (r && r.zones) setZones(r.zones);
      else if (r && r.items) setZones(r.items);
      else if (Array.isArray(r)) setZones(r);
    }).catch(() => {});
    api.get('/api/noc/vlans?allowed_only=true').then(r => {
      if (r && r.vlans) setAllowedVlans(r.vlans);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const syncAuthorized = useCallback(async (oltId) => {
    setSyncing(p => ({ ...p, [oltId]: true }));
    try {
      const res = await api.post(`/api/noc/olts/${oltId}/sync-authorized`, {});
      if (res?.success) {
        showToast(`Sync complete: ${res.synced_count} ONTs synced from OLT`);
        const scanRes = await api.post(`/api/noc/olts/${oltId}/scan-autofind`, { force_refresh: true });
        const onus = scanRes?.onus || [];
        setScanResults(p => ({ ...p, [oltId]: { onus, scannedAt: new Date(), method: scanRes?.method } }));
        setScanReports(p => ({ ...p, [oltId]: { status: 'done', stage: 'result_sync', step: 'Post-sync scan completed', steps: [{ stage: 'result_sync', step: 'Post-sync scan completed', detail: 'Pending ONU cache refreshed after authorized ONT synchronization', elapsed: 0 }], elapsed: 0, result: { count: scanRes?.count ?? onus.length, method: scanRes?.method, scan_evidence: scanRes?.scan_evidence || {} } } }));
      } else {
        showToast(`Sync failed: ${res?.error || 'Unknown error'}`, 'error');
      }
    } catch(e) { showToast(`Sync error: ${String(e)}`, 'error'); }
    setSyncing(p => ({ ...p, [oltId]: false }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scanOLT = useCallback(async (oltId, forceRefresh = false) => {
    if (!forceRefresh) {
      try {
        const scanRes = await api.post(`/api/noc/olts/${oltId}/scan-autofind`, { force_refresh: false });
        if (scanRes?.cached) {
          const onus = scanRes?.onus || [];
          const scannedAt = scanRes?.scanned_at ? new Date(scanRes.scanned_at) : new Date();
          setScanResults(p => ({ ...p, [oltId]: { onus, scannedAt, method: scanRes?.method, isCached: true, cacheAgeSeconds: scanRes?.cache_age_seconds || 0 } }));
          setScanReports(p => ({ ...p, [oltId]: { status: 'done', stage: 'result_sync', step: 'Cached OLT discovery result loaded', steps: [{ stage: 'result_sync', step: 'Cached OLT discovery result loaded', detail: 'No new OLT query was issued because the retained result is within the configured cache window', elapsed: 0 }], elapsed: 0, result: { count: scanRes?.count ?? onus.length, method: scanRes?.method, scan_evidence: scanRes?.scan_evidence || { cached: true, cache_age_seconds: scanRes?.cache_age_seconds || 0 } } } }));
          return;
        }
      } catch {}
    }
    setScanning(p => ({ ...p, [oltId]: true }));
    setScanProgress(p => ({ ...p, [oltId]: { step: 'Starting scan...', steps: [], elapsed: 0, status: 'running' } }));
    try {
      const jobRes = await api.post(`/api/noc/olts/${oltId}/scan-autofind-async`, { force_refresh: true });
      const jobId = jobRes?.job_id;
      if (!jobId) throw new Error('No job_id returned');
      const startTime = Date.now();
      const MAX_WAIT = 70000;
      await new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const elapsed = Math.round((Date.now() - startTime) / 100) / 10;
            if (elapsed * 1000 > MAX_WAIT) {
              clearInterval(interval);
              setScanProgress(p => ({ ...p, [oltId]: { step: 'Timeout — scan took too long', steps: [], elapsed, status: 'error' } }));
              reject(new Error('Scan timeout'));
              return;
            }
            const status = await api.get(`/api/noc/jobs/${jobId}/status`);
            const progressSnapshot = { step: status.step, steps: status.steps || [], elapsed: status.elapsed, status: status.status, stage: status.stage, result: status.result, error: status.error };
            setScanProgress(p => ({ ...p, [oltId]: progressSnapshot }));
            if (status.status === 'done') {
              clearInterval(interval);
              const onus = status.result?.onus || [];
              setScanResults(p => ({ ...p, [oltId]: { onus, scannedAt: new Date(), method: status.result?.method || 'ssh', isCached: false, cacheAgeSeconds: 0 } }));
              setScanReports(p => ({ ...p, [oltId]: progressSnapshot }));
              resolve();
            } else if (status.status === 'error') {
              clearInterval(interval);
              const errorSnapshot = { ...progressSnapshot, step: `Error: ${status.error}`, status: 'error', stage: 'error' };
              setScanProgress(p => ({ ...p, [oltId]: errorSnapshot }));
              setScanReports(p => ({ ...p, [oltId]: errorSnapshot }));
              reject(new Error(status.error));
            }
          } catch {}
        }, 1000);
      });
    } catch (e) {
      try {
        const scanRes = await api.post(`/api/noc/olts/${oltId}/scan-autofind`, { force_refresh: true });
        const onus = scanRes?.onus || [];
        const scannedAt = scanRes?.scanned_at ? new Date(scanRes.scanned_at) : new Date();
        setScanResults(p => ({ ...p, [oltId]: { onus, scannedAt, method: scanRes?.method, isCached: false, cacheAgeSeconds: 0 } }));
        setScanReports(p => ({ ...p, [oltId]: { status: 'done', stage: 'result_sync', step: 'Fallback OLT scan completed', steps: [{ stage: 'result_sync', step: 'Fallback OLT scan completed', detail: 'The direct scan endpoint returned a completed discovery result', elapsed: 0 }], elapsed: 0, result: { count: scanRes?.count ?? onus.length, method: scanRes?.method, scan_evidence: scanRes?.scan_evidence || {} } } }));
      } catch {
        setScanResults(p => ({ ...p, [oltId]: { onus: [], scannedAt: new Date(), isCached: false } }));
        setScanReports(p => ({ ...p, [oltId]: { status: 'error', stage: 'error', step: 'Autofind scan could not return a result', steps: [{ stage: 'error', step: 'Autofind scan could not return a result', detail: 'Neither the asynchronous job nor the fallback scan endpoint returned usable discovery data', elapsed: 0 }], elapsed: 0, error: 'No completed scan result was returned', result: {} } }));
      }
    }
    setScanning(p => ({ ...p, [oltId]: false }));
    setTimeout(() => setScanProgress(p => { const n = {...p}; delete n[oltId]; return n; }), 3000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Do not auto-run live OLT discovery whenever this page mounts or the
  // selector refreshes. Scans are explicit, deterministic user actions.
  // This prevents competing jobs and keeps the last completed result stable.

  const visibleOlts = selectedOlts === 'all' ? olts : olts.filter(o => String(o.id) === selectedOlts);

  const handleIgnore = async (oltId, sn) => {
    try { await api.post(`/api/noc/olts/${oltId}/ignore-onu`, { sn }); showToast(`ONU ${sn} ignored`); scanOLT(oltId); }
    catch { showToast('Failed to ignore', 'error'); }
  };

  const handleAuthorize = async (form) => {
    try {
      const res = await api.post('/api/noc/onus/authorize-verified', form);
      if (res?.success) {
        showToast('ONU authorized successfully!');
        setAuthorizeONU(null);
        // Remove from scan results
        setScanResults(p => {
          const n = { ...p };
          Object.keys(n).forEach(oltId => {
            if (n[oltId]?.onus) n[oltId] = { ...n[oltId], onus: n[oltId].onus.filter(o => o.sn !== form.sn) };
          });
          return n;
        });
        setSelectedSNs(p => { const n = new Set(p); n.delete(form.sn); return n; });
        await syncAuthorized(form.olt_id);
      } else {
        showToast(`Authorization failed: ${res?.error || 'Unknown error'}`, 'error');
      }
      return res;
    } catch(e) {
      showToast('Authorization failed', 'error');
      return { success: false, error: String(e) };
    }
  };

  // Aggregate all ONUs from all visible OLTs
  const allOnus = useMemo(() => {
    const result = [];
    visibleOlts.forEach(olt => {
      const res = scanResults[olt.id];
      (res?.onus || []).forEach(onu => result.push({ ...onu, _oltId: olt.id, _oltName: olt.name }));
    });
    return result;
  }, [visibleOlts, scanResults]);

  // Filter + search
  const filteredOnus = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allOnus;
    return allOnus.filter(o =>
      (o.sn || '').toLowerCase().includes(q) ||
      (o.model || '').toLowerCase().includes(q) ||
      (o.port || o.frame_slot_port || '').toLowerCase().includes(q) ||
      (o.vendor || '').toLowerCase().includes(q)
    );
  }, [allOnus, search]);

  // Sort
  const sortedOnus = useMemo(() => {
    const arr = [...filteredOnus];
    if (sortBy === 'model') arr.sort((a, b) => (a.model || '').localeCompare(b.model || ''));
    else if (sortBy === 'detected') arr.sort((a, b) => (b.detected_at || '').localeCompare(a.detected_at || ''));
    else arr.sort((a, b) => (a.port || a.frame_slot_port || '').localeCompare(b.port || b.frame_slot_port || ''));
    return arr;
  }, [filteredOnus, sortBy]);

  // Group by port
  const portGroups = useMemo(() => {
    const map = new Map();
    sortedOnus.forEach(onu => {
      const port = onu.port || onu.frame_slot_port || 'Unknown';
      if (!map.has(port)) map.set(port, []);
      map.get(port).push(onu);
    });
    return Array.from(map.entries());
  }, [sortedOnus]);

  const allPorts = useMemo(() => [...new Set(allOnus.map(o => o.port || o.frame_slot_port || 'Unknown'))].sort(), [allOnus]);
  const totalOnus = allOnus.length;
  const anyScanning = visibleOlts.some(o => scanning[o.id]);
  const hasCompletedScanReport = visibleOlts.some(o => Boolean(scanReports[o.id]));
  const timeAgo = (d) => {
    if (!d) return 'Never';
    const s = Math.floor((new Date() - d) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    return `${Math.floor(s / 3600)}h ago`;
  };

  const handleSelectSN = (sn, checked) => {
    setSelectedSNs(p => { const n = new Set(p); checked ? n.add(sn) : n.delete(sn); return n; });
  };

  return (
    <div>
      {/* Scan console animation CSS */}
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes nocRadar { to { transform: rotate(360deg); } }
        @keyframes nocPulse { 0%,100% { opacity:.45; transform:scale(.88); } 50% { opacity:1; transform:scale(1.12); } }
        @keyframes nocGrid { from { background-position:0 0,0 0; } to { background-position:28px 28px,28px 28px; } }
        @keyframes nocBeam { from { background-position:180% 0; } to { background-position:-80% 0; } }
        .noc-scan-console { position:relative; overflow:hidden; margin-bottom:14px; border-radius:12px; border:1px solid #204b7a; background:linear-gradient(135deg,#071424 0%,#0a2038 52%,#061522 100%); color:#dceeff; box-shadow:0 12px 28px rgba(3,16,31,.2), inset 0 1px 0 rgba(143,221,255,.09); }
        .noc-scan-console.is-error { border-color:#9e3d4c; background:linear-gradient(135deg,#21101a,#2a121d); }
        .noc-scan-grid { position:absolute; inset:0; opacity:.24; pointer-events:none; background-image:linear-gradient(rgba(102,205,255,.12) 1px,transparent 1px),linear-gradient(90deg,rgba(102,205,255,.12) 1px,transparent 1px); background-size:28px 28px; animation:nocGrid 3s linear infinite; mask-image:linear-gradient(to right,transparent,black 16%,black 84%,transparent); }
        .noc-scan-topline { position:relative; display:flex; align-items:center; gap:8px; min-height:30px; padding:0 16px; border-bottom:1px solid rgba(122,208,255,.16); font:700 10px/1.2 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.1em; color:#83cfff; }
        .noc-scan-live-dot { width:7px; height:7px; border-radius:50%; background:#33e4a1; box-shadow:0 0 0 4px rgba(51,228,161,.12),0 0 12px #33e4a1; animation:nocPulse 1.25s ease-in-out infinite; }
        .is-error .noc-scan-live-dot { background:#ff7081; box-shadow:0 0 0 4px rgba(255,112,129,.12),0 0 12px #ff7081; }
        .noc-scan-divider { width:1px; height:10px; background:rgba(160,221,255,.28); }
        .noc-scan-elapsed { margin-left:auto; color:#b7d9ef; letter-spacing:.03em; font-weight:600; }
        .noc-scan-main { position:relative; display:grid; grid-template-columns:80px minmax(0,1fr) auto; gap:16px; align-items:center; padding:16px; }
        .noc-radar-shell { position:relative; width:68px; height:68px; border:1px solid rgba(96,213,255,.48); border-radius:50%; background:radial-gradient(circle,rgba(35,177,235,.19) 0 8%,transparent 9% 38%,rgba(35,177,235,.08) 39% 40%,transparent 41%); box-shadow:inset 0 0 24px rgba(18,190,255,.16),0 0 20px rgba(20,180,255,.1); }
        .noc-radar-ring { position:absolute; border:1px solid rgba(87,207,255,.34); border-radius:50%; inset:9px; }
        .noc-radar-ring.ring-two { inset:21px; border-color:rgba(87,207,255,.48); }
        .noc-radar-sweep { position:absolute; inset:-1px; border-radius:50%; background:conic-gradient(from 30deg,transparent 0deg,transparent 272deg,rgba(41,220,255,.46) 324deg,transparent 360deg); animation:nocRadar 2.1s linear infinite; }
        .noc-radar-core { position:absolute; inset:23px; display:grid; place-items:center; color:#b9f4ff; border-radius:50%; background:#0b2a43; box-shadow:0 0 16px rgba(82,233,255,.7); }
        .noc-scan-label { margin-bottom:5px; font:700 9px/1 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.13em; color:#72bce8; }
        .noc-scan-step { min-height:20px; font-size:14px; line-height:1.35; font-weight:700; color:#f1fbff; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .noc-scan-progress-track { position:relative; height:5px; overflow:visible; margin:11px 0 9px; border-radius:999px; background:#153b5b; box-shadow:inset 0 1px 2px rgba(0,0,0,.45); }
        .noc-scan-progress-fill { height:100%; border-radius:inherit; background:linear-gradient(90deg,#1f8fff,#35e4bc,#58c8ff,#1f8fff); background-size:180% 100%; animation:nocBeam 1.4s linear infinite; box-shadow:0 0 12px rgba(67,225,255,.7); transition:width .7s ease; }
        .noc-scan-progress-glow { position:absolute; top:50%; width:10px; height:10px; border-radius:50%; background:#d5ffff; transform:translate(-50%,-50%); box-shadow:0 0 0 3px rgba(91,231,255,.16),0 0 16px #50e7ff; transition:left .7s ease; }
        .noc-scan-stages { display:flex; align-items:center; gap:7px; font:700 9px/1 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.08em; color:#537990; }
        .noc-scan-stages span.is-active { color:#76f5cf; text-shadow:0 0 10px rgba(77,243,195,.6); }
        .noc-scan-stages i { width:18px; height:1px; background:linear-gradient(90deg,#347297,transparent); }
        .noc-scan-metrics { display:flex; gap:14px; padding-left:15px; border-left:1px solid rgba(117,203,247,.18); }
        .noc-scan-metrics div { display:flex; flex-direction:column; gap:4px; min-width:42px; }
        .noc-scan-metrics span { font:700 8px/1 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.1em; color:#6394b1; }
        .noc-scan-metrics strong { font:700 11px/1.1 ui-monospace,SFMono-Regular,Menlo,monospace; color:#e1f7ff; }
        .noc-scan-metrics strong.ok { color:#64f0b5; } .noc-scan-metrics strong.error { color:#ff8c99; }
        .noc-scan-detail-zone { position:relative; padding:12px 16px 13px; border-top:1px solid rgba(122,208,255,.14); background:rgba(3,14,27,.34); }
        .noc-detail-heading { display:flex; justify-content:space-between; align-items:center; margin-bottom:9px; color:#77c7ed; font:700 9px/1 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.12em; }
        .noc-detail-heading small { color:#416c85; font-size:8px; letter-spacing:.09em; }
        .noc-activity-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:7px; }
        .noc-activity-card { display:flex; min-width:0; align-items:flex-start; gap:7px; padding:7px 8px; border:1px solid rgba(112,183,223,.12); border-radius:7px; background:rgba(13,36,55,.4); transition:background .25s,border-color .25s,box-shadow .25s; }
        .noc-activity-card .noc-activity-state { flex:0 0 18px; width:18px; height:18px; display:grid; place-items:center; border:1px solid #3f6982; border-radius:50%; color:#6c92a6; font:700 8px/1 ui-monospace,SFMono-Regular,Menlo,monospace; }
        .noc-activity-card strong { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:#7194a7; font:700 8px/1.2 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.06em; }
        .noc-activity-card span { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; margin-top:3px; color:#48687a; font-size:9px; line-height:1.2; }
        .noc-activity-card.active { border-color:rgba(74,224,255,.55); background:linear-gradient(110deg,rgba(17,94,133,.46),rgba(13,54,78,.28)); box-shadow:0 0 16px rgba(46,207,255,.11); }
        .noc-activity-card.active .noc-activity-state { color:#dfffff; border-color:#66ecff; background:#0c4b69; box-shadow:0 0 10px rgba(74,224,255,.7); animation:nocPulse 1.1s ease-in-out infinite; }
        .noc-activity-card.active strong { color:#c7f8ff; } .noc-activity-card.active span { color:#8ac7dc; }
        .noc-activity-card.done { border-color:rgba(70,221,172,.22); background:rgba(17,81,71,.23); }
        .noc-activity-card.done .noc-activity-state { color:#73f0bc; border-color:#3cb589; background:#0d4a3d; } .noc-activity-card.done strong { color:#9ce9c9; } .noc-activity-card.done span { color:#65a990; }
        .noc-activity-card.error { border-color:rgba(255,116,132,.48); background:rgba(99,22,37,.26); } .noc-activity-card.error .noc-activity-state { color:#ffb0bb; border-color:#df6371; } .noc-activity-card.error strong { color:#ffd1d7; }
        .noc-scan-log { position:relative; display:flex; gap:10px; padding:9px 16px 11px; border-top:1px solid rgba(122,208,255,.14); background:rgba(2,10,20,.31); }
        .noc-log-prompt { flex:0 0 auto; padding-top:2px; color:#4bc7f6; font:700 9px/1 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.12em; }
        .noc-log-lines { display:grid; gap:3px; min-width:0; color:#789aaf; font:500 10px/1.25 ui-monospace,SFMono-Regular,Menlo,monospace; }
        .noc-log-lines div { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; opacity:.62; } .noc-log-lines div.current { color:#e0f5ff; opacity:1; } .noc-log-lines span { display:inline-block; width:28px; color:#4ea9d0; }
        .noc-last-scan-strip { display:flex; align-items:center; justify-content:space-between; gap:12px; margin:0 0 12px; padding:9px 11px 9px 12px; border:1px solid #d6e4ec; border-radius:9px; background:linear-gradient(90deg,#f8fcff,#f2f9fc); box-shadow:0 2px 7px rgba(26,71,97,.05); }
        .noc-last-scan-meta { display:flex; align-items:center; gap:8px; min-width:0; } .noc-last-scan-dot { flex:0 0 auto; width:8px; height:8px; border-radius:50%; background:#2ecb91; box-shadow:0 0 0 4px rgba(46,203,145,.12); }
        .noc-last-scan-meta strong { display:block; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:#31536a; font-size:11px; } .noc-last-scan-meta span:not(.noc-last-scan-dot) { display:block; margin-top:2px; color:#7190a2; font-size:10px; }
        .noc-scan-again-btn { flex:0 0 auto; display:inline-flex; align-items:center; gap:6px; padding:8px 12px; border:1px solid #0879b9; border-radius:7px; background:linear-gradient(135deg,#0b83c5,#086ba5); color:#fff; box-shadow:0 3px 8px rgba(13,117,176,.22); cursor:pointer; font:700 10px/1 ui-monospace,SFMono-Regular,Menlo,monospace; letter-spacing:.05em; transition:transform .15s,box-shadow .15s,opacity .15s; }
        .noc-scan-again-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 5px 12px rgba(13,117,176,.28); } .noc-scan-again-btn:disabled { cursor:not-allowed; opacity:.62; }
        @media (max-width:760px) { .noc-activity-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
        @media (max-width:640px) { .noc-scan-main { grid-template-columns:60px minmax(0,1fr); gap:12px; padding:13px; } .noc-radar-shell { transform:scale(.82); transform-origin:left center; } .noc-scan-metrics { display:none; } .noc-scan-step { font-size:12px; } .noc-scan-topline { padding:0 12px; font-size:8px; } .noc-scan-divider, .noc-scan-topline > span:nth-of-type(3) { display:none; } .noc-activity-grid { grid-template-columns:1fr; } .noc-activity-card { padding:7px; } .noc-last-scan-strip { align-items:flex-start; flex-direction:column; } .noc-scan-again-btn { width:100%; justify-content:center; } }
      `}</style>
      {/* Toast */}
      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.type === 'error' ? '#c62828' : '#2e7d32', color: '#fff', fontWeight: 600, boxShadow: '0 4px 16px rgba(0,0,0,0.2)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
        {toast.type === 'error' ? '❌' : '✅'} {toast.msg}
      </div>}

      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1A1C1E' }}>ONU Authorization</h2>
        <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>Detect, authorize, and manage ONUs across all OLT devices</p>
      </div>

      {/* ── Active scan workspace — absolute top, above authorization tabs ── */}
      {visibleOlts.map(olt => {
        const progress = scanProgress[olt.id];
        const isTerminal = progress?.status === 'done' || progress?.status === 'error';
        return progress && !isTerminal ? (
          <div key={olt.id} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6, color: '#31536a', fontSize: 11, fontWeight: 750 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#2ecb91', boxShadow: '0 0 0 4px rgba(46,203,145,.12)' }} />
              Scan workspace · {olt.name}
            </div>
            <ScanProgress prog={progress} olt={olt} />
          </div>
        ) : null;
      })}

      {/* ── Authorization tabs ── */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e0e0e0' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => onTabChange(t.id)} style={{
            background: 'none', border: 'none', padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
            color: t.id === 'unconfigured' ? '#1565C0' : '#666',
            borderBottom: t.id === 'unconfigured' ? '2px solid #1565C0' : '2px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <select value={selectedOlts} onChange={e => setSelectedOlts(e.target.value)} style={{ border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 13, minWidth: 160 }}>
          <option value="all">All OLTs ({olts.length})</option>
          {olts.map(o => <option key={o.id} value={String(o.id)}>{o.id} – {o.name}</option>)}
        </select>
        {/* Search */}
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search SN, model, port..."
            style={{ width: '100%', border: '1px solid #ccc', borderRadius: 6, padding: '7px 12px 7px 32px', fontSize: 13, boxSizing: 'border-box' }}
          />
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9e9e9e', fontSize: 14 }}>🔍</span>
        </div>
        {/* Sort */}
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ border: '1px solid #ccc', borderRadius: 6, padding: '7px 10px', fontSize: 13 }}>
          <option value="port">Sort: Port</option>
          <option value="model">Sort: Model</option>
          <option value="detected">Sort: Detected time</option>
        </select>
        {/* Refresh */}
        <button onClick={() => visibleOlts.forEach(o => scanOLT(o.id, true))} disabled={anyScanning} style={{ background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: anyScanning ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, opacity: anyScanning ? 0.7 : 1 }}>
          <IconScan size={13} /> {anyScanning ? 'Scanning...' : 'Refresh Autofind'}
        </button>
        {/* Sync */}
        <button onClick={() => visibleOlts.forEach(o => syncAuthorized(o.id))} disabled={visibleOlts.some(o => syncing[o.id])} style={{ background: '#1565C0', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, opacity: visibleOlts.some(o => syncing[o.id]) ? 0.7 : 1 }}>
          <IconSync size={13} /> Sync Now
        </button>
      </div>

      {/* ── Summary + Bulk Actions ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: '#546e7a', fontWeight: 500 }}>
          {totalOnus > 0
            ? `${totalOnus} unconfigured ONU${totalOnus !== 1 ? 's' : ''} across ${allPorts.length} port${allPorts.length !== 1 ? 's' : ''}`
            : 'No unconfigured ONUs'}
          {search && ` · ${filteredOnus.length} matching`}
        </span>
        {selectedSNs.size > 0 && (
          <button
            disabled={bulkAuthorizing}
            onClick={() => {
              const first = allOnus.find(o => selectedSNs.has(o.sn));
              if (first) setAuthorizeONU({ ...first, olt_id: first._oltId });
            }}
            style={{ background: '#1565C0', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}
          >
            ✓ Authorize Selected ({selectedSNs.size})
          </button>
        )}
        {selectedSNs.size > 0 && (
          <button onClick={() => setSelectedSNs(new Set())} style={{ background: '#fff', color: '#546e7a', border: '1px solid #cfd8dc', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}>
            Clear
          </button>
        )}
      </div>

      {/* ── Last scan info ── */}
      {visibleOlts.map(olt => {
        const res = scanResults[olt.id];
        if (!res || scanning[olt.id]) return null;
        return (
          <div key={olt.id}>
            <div className="noc-last-scan-strip">
              <div className="noc-last-scan-meta">
                <span className="noc-last-scan-dot" />
                <div><strong>{olt.name}</strong><span>Last scan {res.scannedAt ? timeAgo(res.scannedAt) : '—'}{res.isCached ? ' · cached data' : ' · live OLT result'}</span></div>
              </div>
              <button type="button" className="noc-scan-again-btn" onClick={() => scanOLT(olt.id, true)} disabled={!!scanning[olt.id]}>
                <IconScan size={14} /> {scanning[olt.id] ? 'SCAN IN PROGRESS' : 'SCAN AGAIN'}
              </button>
            </div>
            <ScanEvidenceReport prog={scanReports[olt.id]} olt={olt} />
          </div>
        );
      })}

      {/* ── ONU Cards ── */}
      {anyScanning && allOnus.length === 0 ? (
        <div>
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      ) : totalOnus === 0 && !hasCompletedScanReport ? (
        <EmptyState onRefresh={() => visibleOlts.forEach(o => scanOLT(o.id, true))} />
      ) : portGroups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: '#9e9e9e', fontSize: 13 }}>No ONUs match your search</div>
      ) : (
        portGroups.map(([port, onus]) => {
          const portColor = getPortColor(port, allPorts);
          const oltId = onus[0]?._oltId;
          return (
            <PortGroup
              key={port}
              port={port}
              onus={onus}
              oltId={oltId}
              portColor={portColor}
              onAuthorize={setAuthorizeONU}
              onIgnore={handleIgnore}
              selectedSNs={selectedSNs}
              onSelectSN={handleSelectSN}
              defaultOpen={true}
            />
          );
        })
      )}

      {/* ── Bottom actions ── */}
      <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
        <button onClick={() => setAuthorizeONU({})} style={{ background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>+ Add ONU manually</button>
      </div>

      {authorizeONU !== null && <AuthorizeONUModal onu={authorizeONU} olts={olts} zones={zones} allowedVlans={allowedVlans} onClose={() => setAuthorizeONU(null)} onSave={handleAuthorize} />}
    </div>
  );
}

// ─── NewONUsTab ───────────────────────────────────────────────────────────────
function NewONUsTab({ olts, api }) {
  const [onus, setOnus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authorizeONU, setAuthorizeONU] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/api/noc/onus?status=unconfigured').then(d => {
      setOnus(Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : []);
    }).catch(() => setOnus([])).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthorize = async (form) => {
    await api.post('/api/noc/onus/authorize-verified', form);
    setAuthorizeONU(null);
    api.get('/api/noc/onus?status=unconfigured').then(d => setOnus(Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : []));
  };

  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>ONUs authorized within the last 24 hours</div>
      {loading ? <div style={{ color: '#888', fontSize: 13 }}>Loading...</div> : (
        <div>
          {onus.length === 0 ? <div style={{ color: '#888', fontSize: 13 }}>No recently authorized ONUs.</div> : onus.map(o => (
            <div key={o.id || o.onu_serial} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 14px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
              <span>
                <strong style={{ fontFamily: 'monospace' }}>{o.onu_serial || o.sn}</strong>
                {o.frame_slot_port && <> &nbsp;|&nbsp; {o.frame_slot_port}</>}
                {o.description && <> &nbsp;|&nbsp; {o.description}</>}
              </span>
              <button onClick={() => setAuthorizeONU(o)} style={{ background: '#e3f2fd', color: '#1565C0', border: 'none', borderRadius: 4, padding: '3px 10px', cursor: 'pointer', fontSize: 12 }}>View</button>
            </div>
          ))}
        </div>
      )}
      {authorizeONU !== null && <AuthorizeONUModal onu={authorizeONU} olts={olts} zones={zones} onClose={() => setAuthorizeONU(null)} onSave={handleAuthorize} />}
    </div>
  );
}

// ─── AllONUsTab ───────────────────────────────────────────────────────────────
function AllONUsTab({ olts, api }) {
  const [onus, setOnus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterOlt, setFilterOlt] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [authorizeONU, setAuthorizeONU] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  const loadOnus = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.get('/api/noc/onus');
      setOnus(Array.isArray(d?.items) ? d.items : Array.isArray(d) ? d : []);
    } catch { setOnus([]); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadOnus(); }, [loadOnus]);

  const handleSyncAll = async () => {
    setSyncing(true);
    let total = 0;
    for (const olt of olts) {
      try {
        const res = await api.post(`/api/noc/olts/${olt.id}/sync-authorized`, {});
        if (res?.success) total += res.synced_count || 0;
      } catch {}
    }
    showToast(`Sync complete: ${total} ONTs updated`);
    await loadOnus();
    setSyncing(false);
  };

  const filtered = onus.filter(o => {
    const q = search.toLowerCase();
    const matchSearch = !q || (o.onu_serial || o.sn || '').toLowerCase().includes(q) || (o.description || o.name || '').toLowerCase().includes(q) || (o.pppoe_username || o.pppoe || '').toLowerCase().includes(q);
    const matchOlt = !filterOlt || String(o.olt_id) === filterOlt;
    const matchStatus = !filterStatus || o.status === filterStatus;
    return matchSearch && matchOlt && matchStatus;
  });

  const stats = { total: onus.length, online: onus.filter(o => o.status === 'online').length, offline: onus.filter(o => o.status === 'offline').length, unconfigured: onus.filter(o => o.status === 'unconfigured').length };

  const handleAuthorize = async (form) => {
    const res = await api.post('/api/noc/onus/authorize-verified', form);
    if (res && (res.success || res.status === 'success' || res.telnet_result === 'success')) {
      setAuthorizeONU(null);
      loadOnus();
      setToast({ msg: 'ONU authorized successfully!', type: 'success' });
      setTimeout(() => setToast(null), 4000);
    }
    return res;
  };
  const handleDelete = async (id) => { if (!window.confirm('Delete this ONU?')) return; await api.del(`/api/noc/onus/${id}`); loadOnus(); };

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '10px 20px', borderRadius: 6, background: toast.type === 'error' ? '#c62828' : '#2e7d32', color: '#fff', fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', fontSize: 13 }}>{toast.msg}</div>}
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        {[{ label: 'Total ONUs', val: stats.total, color: '#1565C0' }, { label: 'Online', val: stats.online, color: '#2e7d32' }, { label: 'Offline', val: stats.offline, color: '#c62828' }, { label: 'Unconfigured', val: stats.unconfigured, color: '#e65100' }].map(s => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '14px 18px' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>
      {/* Filters + Sync */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search serial, PPPoE, description..." style={{ flex: 1, minWidth: 200, border: '1px solid #ccc', borderRadius: 4, padding: '7px 12px', fontSize: 13 }} />
        <select value={filterOlt} onChange={e => setFilterOlt(e.target.value)} style={{ border: '1px solid #ccc', borderRadius: 4, padding: '7px 10px', fontSize: 13 }}>
          <option value="">All OLTs</option>
          {olts.map(o => <option key={o.id} value={String(o.id)}>{o.id} – {o.name}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ border: '1px solid #ccc', borderRadius: 4, padding: '7px 10px', fontSize: 13 }}>
          <option value="">All Status</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="unconfigured">Unconfigured</option>
        </select>
        <button onClick={loadOnus} style={{ background: '#fff', color: '#555', border: '1px solid #ccc', borderRadius: 4, padding: '7px 12px', cursor: 'pointer', fontSize: 13 }}>↺ Refresh</button>
        <button onClick={handleSyncAll} disabled={syncing} style={{ background: '#1565C0', color: '#fff', border: 'none', borderRadius: 4, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: syncing ? 0.6 : 1 }}>
          {syncing ? '⏳ Syncing...' : '↺ Sync All OLTs'}
        </button>
      </div>
      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
              {['Status','Serial Number','OLT','F/S/P','ONT ID','ODB','PPPoE','Description','Last Seen','Actions'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700 }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading ONUs...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 40, textAlign: 'center', color: '#888' }}>No ONUs found. Sync an OLT first.</td></tr>
            ) : filtered.map((o, i) => (
              <tr key={o.id || o.onu_serial} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={{ padding: '7px 10px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: o.status === 'online' ? '#e8f5e9' : o.status === 'unconfigured' ? '#fff3e0' : '#ffebee', color: o.status === 'online' ? '#2e7d32' : o.status === 'unconfigured' ? '#e65100' : '#c62828', borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: o.status === 'online' ? '#2e7d32' : o.status === 'unconfigured' ? '#e65100' : '#c62828', display: 'inline-block' }}></span>
                    {o.status === 'online' ? 'Online' : o.status === 'unconfigured' ? 'Unconfigured' : 'Offline'}
                  </span>
                </td>
                <td style={{ padding: '7px 10px', fontFamily: 'monospace', fontWeight: 600 }}>{o.onu_serial || o.sn}</td>
                <td style={{ padding: '7px 10px' }}>{o.olt_name || '—'}</td>
                <td style={{ padding: '7px 10px' }}>{o.frame_slot_port || o.port || '—'}</td>
                <td style={{ padding: '7px 10px' }}>{o.ont_id != null ? o.ont_id : '—'}</td>
                <td style={{ padding: '7px 10px' }}>{o.odb_splitter || o.odb || '—'}</td>
                <td style={{ padding: '7px 10px' }}>{o.pppoe_username || o.pppoe || '—'}</td>
                <td style={{ padding: '7px 10px' }}>{o.description || o.name || '—'}</td>
                <td style={{ padding: '7px 10px', fontSize: 11, color: '#888' }}>{o.last_seen ? new Date(o.last_seen).toLocaleString() : '—'}</td>
                <td style={{ padding: '7px 10px' }}>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => setAuthorizeONU(o)} style={{ background: '#e3f2fd', color: '#1565C0', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}>View</button>
                    <button style={{ background: '#fff8e1', color: '#e65100', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}>Disable</button>
                    <button onClick={() => handleDelete(o.id)} style={{ background: '#ffebee', color: '#c62828', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {authorizeONU !== null && <AuthorizeONUModal onu={authorizeONU} olts={olts} zones={zones} onClose={() => setAuthorizeONU(null)} onSave={handleAuthorize} />}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function ONUAuthorizationPageInner() {
  const [tab, setTab] = useState('unconfigured');
  const [olts, setOlts] = useState([]);
  const api = useApi();

  useEffect(() => {
    api.get('/api/noc/olts').then(d => setOlts(Array.isArray(d) ? d : [])).catch(() => setOlts([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = [
    { id: 'unconfigured', label: 'Unconfigured ONUs' },
    { id: 'new', label: 'New ONUs' },
    { id: 'all', label: 'All ONUs' },
  ];

  const renderTabs = () => (
    <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderBottom: '2px solid #e0e0e0' }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => setTab(t.id)} style={{
          background: 'none', border: 'none', padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
          color: tab === t.id ? '#1565C0' : '#666',
          borderBottom: tab === t.id ? '2px solid #1565C0' : '2px solid transparent',
          marginBottom: -2,
        }}>{t.label}</button>
      ))}
    </div>
  );

  return (
    <div style={{ padding: 20, fontFamily: 'Roboto,sans-serif', background: '#FAFBFF', minHeight: '100vh' }}>
      {tab === 'unconfigured' ? (
        <UnconfiguredTab olts={olts} api={api} tabs={tabs} onTabChange={setTab} />
      ) : <>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1A1C1E' }}>ONU Authorization</h2>
        <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>Detect, authorize, and manage ONUs across all OLT devices</p>
      </div>
      {renderTabs()}
      {tab === 'new' && <NewONUsTab olts={olts} api={api} />}
      {tab === 'all' && <AllONUsTab olts={olts} api={api} />}
      </>}
    </div>
  );
}

export default function ONUAuthorizationPage() {
  return (
    <ErrorBoundary>
      <ONUAuthorizationPageInner />
    </ErrorBoundary>
  );
}
